import { fetchAuthSession } from 'aws-amplify/auth';
import {
  BedrockRuntimeClient,
  ConverseStreamCommand,
  type Message,
  type ConverseStreamCommandInput,
  type Tool,
} from '@aws-sdk/client-bedrock-runtime';
import { CHAT_CONFIG } from './chatConfig';
import { CANNABIS_EXPERT_SYSTEM_PROMPT } from './systemPrompt';
import { prepareConversationHistory } from './chatHistory';
import type { ChatError, ChatMessage, StreamCallbacks, ToolDefinition } from './chatTypes';

/**
 * Retrieves an authenticated BedrockRuntimeClient using Cognito session credentials.
 * Returns either the initialized client or a ChatError if credentials are unavailable.
 */
export async function getBedrockClient(): Promise<
  { client: BedrockRuntimeClient } | { error: ChatError }
> {
  try {
    const session = await fetchAuthSession();
    const credentials = session.credentials;

    if (!credentials) {
      return {
        error: {
          type: 'auth',
          message: 'Session expired. Please sign in again.',
          retryable: false,
        },
      };
    }

    const client = new BedrockRuntimeClient({
      region: CHAT_CONFIG.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    });

    return { client };
  } catch {
    return {
      error: {
        type: 'auth',
        message: 'Session expired. Please sign in again.',
        retryable: false,
      },
    };
  }
}

/**
 * Sends a message to the Bedrock Converse Stream API and processes the streaming response.
 *
 * Builds the request with system prompt, conversation history, tool definitions, and
 * inference config. Parses streaming events and invokes callbacks for tokens, completion,
 * and errors. Supports cancellation via AbortSignal with a 30s timeout.
 *
 * Requirements: 5.1, 5.3, 5.7, 7.1, 7.4, 6.5
 */
export async function sendMessage(
  userMessage: string,
  conversationHistory: ChatMessage[],
  callbacks: StreamCallbacks,
  abortSignal?: AbortSignal,
  toolDefinitions?: ToolDefinition[]
): Promise<void> {
  // Get authenticated Bedrock client
  const result = await getBedrockClient();
  if ('error' in result) {
    callbacks.onError(result.error);
    return;
  }
  const { client } = result;

  // Prepare conversation history and append the new user message
  const history = prepareConversationHistory(conversationHistory);
  const messages: Message[] = [
    ...history,
    { role: 'user' as const, content: [{ text: userMessage }] },
  ] as Message[];

  // Build the ConverseStream request with tool config if applicable
  const input: ConverseStreamCommandInput = {
    modelId: CHAT_CONFIG.modelId,
    system: [{ text: CANNABIS_EXPERT_SYSTEM_PROMPT }],
    messages,
    inferenceConfig: {
      maxTokens: CHAT_CONFIG.maxTokens,
    },
  };

  if (toolDefinitions && toolDefinitions.length > 0) {
    const tools: Tool[] = toolDefinitions.map((def) => ({
      toolSpec: {
        name: def.toolSpec.name,
        description: def.toolSpec.description,
        inputSchema: { json: def.toolSpec.inputSchema.json },
      },
    }) as Tool);
    input.toolConfig = { tools };
  }

  const command = new ConverseStreamCommand(input);

  // Set up timeout via AbortController if no external signal provided
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => {
    timeoutController.abort();
  }, CHAT_CONFIG.requestTimeout);

  // Combine external abort signal with timeout
  const effectiveSignal = abortSignal || timeoutController.signal;

  // If external signal is already aborted, short-circuit
  if (effectiveSignal.aborted) {
    clearTimeout(timeoutId);
    callbacks.onError({
      type: 'timeout',
      message: 'The request timed out. Please try again.',
      retryable: true,
    });
    return;
  }

  // Listen for external abort to also clear timeout
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => clearTimeout(timeoutId), { once: true });
  }

  try {
    const response = await client.send(command, {
      abortSignal: effectiveSignal,
    });

    if (!response.stream) {
      clearTimeout(timeoutId);
      callbacks.onError({
        type: 'service',
        message: 'The AI service is temporarily unavailable. Please try again.',
        retryable: true,
      });
      return;
    }

    let fullText = '';
    let stopReason = 'end_turn';

    // Track tool use blocks (detected but not processed here — task 3.1 handles invocation)
    let currentToolUseBlock: { toolUseId: string; name: string; input: string } | null = null;
    const toolUseBlocks: { toolUseId: string; name: string; input: string }[] = [];

    for await (const event of response.stream) {
      // Check for abort between events
      if (effectiveSignal.aborted) {
        clearTimeout(timeoutId);
        callbacks.onError({
          type: 'timeout',
          message: 'The request timed out. Please try again.',
          retryable: true,
        });
        return;
      }

      if (event.contentBlockStart) {
        const start = event.contentBlockStart.start;
        if (start && 'toolUse' in start && start.toolUse) {
          // A tool use block is starting — track it
          currentToolUseBlock = {
            toolUseId: start.toolUse.toolUseId || '',
            name: start.toolUse.name || '',
            input: '',
          };
        }
      }

      if (event.contentBlockDelta) {
        const delta = event.contentBlockDelta.delta;
        if (delta) {
          if ('text' in delta && delta.text) {
            // Text token — accumulate and notify
            fullText += delta.text;
            callbacks.onToken(delta.text);
          } else if ('toolUse' in delta && delta.toolUse && currentToolUseBlock) {
            // Tool use input delta — accumulate the JSON input string
            currentToolUseBlock.input += delta.toolUse.input || '';
          }
        }
      }

      if (event.contentBlockStop) {
        // If we were tracking a tool use block, finalize it
        if (currentToolUseBlock) {
          toolUseBlocks.push(currentToolUseBlock);
          currentToolUseBlock = null;
        }
      }

      if (event.messageStop) {
        stopReason = event.messageStop.stopReason || 'end_turn';
      }
    }

    clearTimeout(timeoutId);
    callbacks.onComplete(fullText, stopReason);
  } catch (error: unknown) {
    clearTimeout(timeoutId);

    // Classify the error
    const chatError = classifyError(error);
    callbacks.onError(chatError);
  }
}

/**
 * Classifies an error from the Bedrock API into a ChatError.
 */
export function classifyError(error: unknown): ChatError {
  // Handle abort/timeout errors
  if (error instanceof Error) {
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      return {
        type: 'timeout',
        message: 'The request timed out. Please try again.',
        retryable: true,
      };
    }

    // Network errors (fetch failures, DNS, etc.)
    if (error.name === 'TypeError' || error.message?.includes('fetch')) {
      return {
        type: 'network',
        message: 'You appear to be offline. Reconnecting...',
        retryable: true,
      };
    }

    // AWS SDK error shapes have a `name` property with the exception type
    const errorName = (error as { name?: string }).name || '';
    const statusCode = (error as { $metadata?: { httpStatusCode?: number } }).$metadata
      ?.httpStatusCode;

    // Throttling errors
    if (
      errorName === 'ThrottlingException' ||
      errorName === 'TooManyRequestsException' ||
      statusCode === 429
    ) {
      return {
        type: 'service',
        message: 'The AI service is temporarily unavailable. Please try again.',
        retryable: true,
      };
    }

    // Auth errors
    if (
      errorName === 'AccessDeniedException' ||
      errorName === 'UnrecognizedClientException' ||
      statusCode === 401 ||
      statusCode === 403
    ) {
      return {
        type: 'auth',
        message: 'Session expired. Please sign in again.',
        retryable: false,
      };
    }
  }

  // Default to service error
  return {
    type: 'service',
    message: 'The AI service is temporarily unavailable. Please try again.',
    retryable: true,
  };
}
